
import { Options, Vue, prop } from "vue-class-component";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  ModalMessageEntities,
  ErrorMessageEntities
} from "@/domain/entities/MainApp";
import { BulkBookingController } from "@/app/ui/controllers/BulkBookingController";
import { DeleteSttRepair } from "@/data/payload/api/BulkApiRequest";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";
import {
  payloadBookingManual,
  payloadBookingCorporate,
  generateMessageModalSuccessPopup
} from "./module";
import { BookingController } from "@/app/ui/controllers/BookingController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import WeightCost from "./components/weight-cost.vue";
import CheckTariffV2 from "./components/check-tariff-v2.vue";
import {
  PromoConfigurationList,
  PromoConfigurationListData
} from "@/domain/entities/PromoConfiguration";
import { PromoConfigurationListRequest } from "@/data/payload/api/PromoConfigurationApiRequest";
import { PromoConfigurationController } from "@/app/ui/controllers/PromoConfigurationController";
import { formatDateWithoutTime } from "@/app/infrastructures/misc/Utils";
import { ManualBooking } from "@/domain/entities/Booking";
import { configPrice } from "@/app/infrastructures/misc/common-library/modules/Price";

const countryCodeToCurrency: { [key: string]: string } = {
  ID: "IDR",
  MY: "MYR"
};

interface DiscountInterface {
  status: boolean;
  data: PromoConfigurationListData[];
}

class Props {
  payload = prop<any>({
    required: true
  });
  isDisabled = prop<boolean>({
    required: true,
    default: false
  });
  formType = prop<"manual" | "client">({
    required: true,
    default: "manual"
  });
  rowNumber = prop<number>({
    required: true,
    default: 1
  });
  bulkId = prop<number>({
    required: true,
    default: 0
  });
  repairPosition = prop<string>({
    required: true,
    default: "1/10"
  });
  totalRepaired = prop<number>({
    required: true,
    default: 1
  });
  totalFailed = prop<number>({
    required: true,
    default: 0
  });
  accountRefAndCommodity = prop<any>({
    required: true
  });
  isCodRetailApplied = prop<boolean>({
    required: true
  });
  totalItem = prop<any>({
    default: "0"
  });
  totalGrossWeight = prop<any>({
    default: "0"
  });
  totalDimensionWeight = prop<any>({
    default: "0"
  });
  chargeableWeight = prop<any>({
    default: "0"
  });
  isVolumeWeight = prop<boolean>({
    required: true
  });
  isCampaign = prop<boolean>({
    required: true
  });
  isGrossWeight = prop<boolean>({
    required: true
  });
  goodsValue = prop<any>({
    default: "0"
  });
  form = prop<ManualBooking>({
    default: () => new ManualBooking(),
    type: Object
  });
  clientIdFromShipment = prop<number>({
    default: 0,
    type: Number
  });
  isCrossCountryCod = prop<boolean>({ default: false, type: Boolean });
  prefixCurrency = prop<string>({ required: true, type: String });
}

@Options({
  components: {
    WeightCost,
    CheckTariffV2
  },
  watch: {
    "payload.productName": function() {
      this.checkDiscountActive();
      this.resetDiscount();
    },
    "payload.destinationCity": function() {
      this.checkDiscountActive();
      this.resetDiscount();
    },
    "payload.sttGrossWeight": function() {
      this.checkDiscountActive();
      this.resetDiscount();
    },
    "payload.sttVolumeWeight": function() {
      this.checkDiscountActive();
      this.resetDiscount();
    }
  }
})
export default class FooterEditBulkBooking extends Vue.with(Props) {
  messageModal = false;
  messageEntities = new ModalMessageEntities();
  statusGoodForClients = "ecommerce";
  defaultInsuranceTypeForClients = "free";
  
  created() {
    window.addEventListener("keydown", this.onEnterBooking);
  }

  unmounted() {
    BookingController.setDefaultManualBooking();
    window.removeEventListener("keydown", this.onEnterBooking);
  }

  get statusGoodValueForclients() {
    return this.statusGoodForClients;
  }

  get payloadBookingManual() {
    return payloadBookingManual({
      ...this.payload,
      bulkId: Number(this.$route.query.id),
      bulkSttRowNumber: this.rowNumber
    });
  }

  get insuranceAsDefault() {
    return this.insuranceForClients;
  }

  set insuranceForClients(value: string) {
    this.defaultInsuranceTypeForClients = value;
  }

  get insuranceForClients() {
    return this.defaultInsuranceTypeForClients;
  }

  get productType() {
    return this.formType === "client"
      ? this.payloadBookingCorporate.productName
      : this.payloadBookingManual.productName;
  }

  get payloadBookingCorporate() {
    return payloadBookingCorporate(
      {
        ...this.payload,
        bulkId: Number(this.$route.query.id),
        bulkSttRowNumber: this.rowNumber
      },
      this.statusGoodValueForclients,
      this.insuranceAsDefault
    );
  }

  get isClientAccount() {
    return AccountController.accountData.isAllClient;
  }

  get estimationPrice() {
    return BookingController.estimationPrice;
  }

  get destinationCurrencyKey() {
    return countryCodeToCurrency[this.form.destinationCity?.countryCode];
  }

  get destinationCurrencyPrefix() {
    return configPrice[this.destinationCurrencyKey]?.prefix ?? "";
  }

  get destinationCurrencyLocale() {
    return configPrice[this.destinationCurrencyKey]?.locales ?? "";
  }

  modalDeleteRepair = false;
  loadingDeleteRepair = false;
  async onDeleteSttRepair() {
    this.loadingDeleteRepair = true;
    this.modalDeleteRepair = false;
    MainAppController.closeMessageModal();
    await BulkBookingController.deleteSttRepair(
      new DeleteSttRepair({
        bulkId: this.bulkId,
        rowNumber: this.rowNumber
      })
    )
      .then(() => {
        this.modalDeleteRepair = true;
        this.showSuccessDeletePopup();
      })
      .finally(() => {
        this.loadingDeleteRepair = false;
      });
  }

  onEnterBooking(event: any) {
    if (event.key !== "Enter") return;
    if (this.isDisabled) return;
    this.onBookingSttRepair();
  }

  onConfirmDeleteSttRepair() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: this.$t("bulkBooking.modal.deleteTitle", {
          value: this.repairPosition.split("/")[0]
        }),
        message: this.$t("bulkBooking.modal.deleteMessage"),
        onSubmit: this.onDeleteSttRepair,
        onClose: MainAppController.closeMessageModal,
        textSuccess: this.$t("bulkBooking.modal.delete"),
        textCancel: this.$t("bulkBooking.modal.back"),
        customWidth: "417px",
        customButton: "w-full",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-6"
      })
    );
  }

  showSuccessDeletePopup() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-success",
        title: this.$t("bulkBooking.edit.successDeleteTitle"),
        message: this.$t("bulkBooking.modal.successDeleteMessage"),
        onSubmit: () => {
          if (
            this.repairPosition.split("/")[0] ===
            this.repairPosition.split("/")[1]
          ) {
            this.$emit("done");
          } else {
            this.$emit("delete");
          }
          MainAppController.closeMessageModal(); // temporary
          this.modalDeleteRepair = false;
        },
        textSuccess: "Ok",
        customWidth: "330px",
        customButton: "w-full",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-6",
        imageHeight: "32",
        imageWidth: "32"
      })
    );
  }

  async validateShipmentCP() {
    if (!this.payload.sttShipmentId) return;
    try {
      MainAppController.showLoading();
      await BulkBookingController.checkCPShipment(this.payload.sttShipmentId);
    } catch (err) {
      MainAppController.showErrorMessage(
        new ErrorMessageEntities({
          type: "client-error",
          title: this.$t("bulkBooking.bookingFailed"),
          message: err,
          onClose: () => {
            document
              .getElementById("repair-shipment-id")
              ?.scrollIntoView({ behavior: "smooth" });
            this.$emit("error-shipment-id");
          }
        })
      );
      throw err;
    } finally {
      MainAppController.closeLoading();
    }
  }

  loadingRepair = false;
  async onBookingSttRepair() {
    if (this.loadingRepair || this.messageModal) return;
    if (this.loadingDeleteRepair || this.modalDeleteRepair) return;
    this.loadingRepair = true;
    await this.validateShipmentCP();
    BulkBookingController.repairBooking({
      formType: this.formType,
      payload:
        this.formType === "manual" || this.isClientAccount
          ? this.payloadBookingManual
          : this.payloadBookingCorporate
    })
      .then((res) => {
        this.showBookingSuccessModal({
          ...res,
          data: {
            ...res.data,
            isCrossCountryCod: this.isCrossCountryCod,
            prefixCurrency: this.prefixCurrency,
            destination: this.form.destinationCountry
          }
        });
      })
      .finally(() => {
        this.loadingRepair = false;
      });
  }

  async showBookingSuccessModal(res: ResponsePayload) {
    this.messageModal = true;
    this.messageEntities = new ModalMessageEntities({
      customWidth: "640px",
      image: "badge-success",
      imageWidth: "32",
      imageheight: "32",
      title: this.$t("bulkBooking.modal.successTitle", {
        value: this.repairPosition
      }),
      message: generateMessageModalSuccessPopup(
        res,
        this.productType,
        this.formType === "client",
        this.isClientAccount
      ),
      textSuccess:
        this.repairPosition.split("/")[0] === this.repairPosition.split("/")[1]
          ? this.$t("bulkBooking.modal.done")
          : this.$t("bulkBooking.modal.nextBooking"),
      textCancel: this.$t("bulkBooking.modal.printThermalReceipt"),
      onSubmit: () => {
        this.messageModal = false;
        if (
          this.repairPosition.split("/")[0] ===
          this.repairPosition.split("/")[1]
        ) {
          this.$emit("done");
        } else {
          this.$emit("next");
        }
      },
      onClose: () => {
        this.onPrintThermalPdf(res);
      },
      customButton: "w-full text-sm",
      customParentButton: "w-full",
      customWrapperButton: "w-full px-6"
    });
  }

  async print(sttId: number, sttNo: string) {
    const book: any = await import(
      "@/app/ui/views/shipment/booking/create-booking/payment-detail/book.vue"
    );

    let language =
      this.productType === "INTERPACK" || this.productType === "INTERPACKMAXI"
        ? "en"
        : "id";
    if (AccountController.accountData.accountIsForeign) language = "en";

    await book.default.methods.printBasic(
      sttId,
      0,
      language,
      false,
      true,
      sttNo
    );
  }

  async onPrintThermalPdf(res: ResponsePayload) {
    this.messageModal = false;
    await this.print(res.sttId, res.sttNumber);
    this.showBookingSuccessModal(res);
  }

  discount: DiscountInterface = {
    status: false,
    data: []
  };

  get isDiscountActive() {
    return (
      this.payload.originCity &&
      this.payload.destinationCity &&
      this.payload.productName &&
      this.payload.sttGrossWeight > 0 &&
      this.formType === "manual" &&
      AccountController.accountData.isPosAccount
    );
  }

  async checkDiscountActive() {
    if (this.isDiscountActive) {
      try {
        const promotion: PromoConfigurationList = await PromoConfigurationController.getPromoActive(
          new PromoConfigurationListRequest({
            page: 1,
            limit: 2,
            status: "active,nearly-expired",
            origin: this.payload.originCity,
            destination: this.payload.destinationCity,
            productName: this.payload.productName,
            grossWeight: this.payload.sttGrossWeight,
            volumeWeight: this.payload.sttVolumeWeight,
            chargeableWeight: this.payload.sttChargeableWeight,
            isTotalData: false,
            appliedTo: "retail"
          })
        );

        const promotionList = promotion.data;
        const isRetail = promotion.data[0].promoConfigurationAppliedTo.includes(
          "retail"
        );
        if (promotionList.length > 0 && isRetail) {
          this.discount = {
            status: true,
            data: promotionList.map((item: PromoConfigurationListData) => {
              return new PromoConfigurationListData({
                promoConfigurationName: `${
                  item.promoConfigurationName
                } sampai dengan ${formatDateWithoutTime(
                  item.promoConfigurationEndDate
                )}`,
                promoConfigurationDiscountNominal:
                  item.promoConfigurationDiscountNominal
              });
            })
          };
        } else {
          this.resetDiscountNominal();
        }
      } catch (err) {
        this.discount.status = false;
      }
    }
  }

  resetDiscount() {
    if (this.payload.sttGrossWeight <= 0 || this.payload.sttVolumeWeight <= 0) {
      this.resetDiscountNominal();
    }
  }

  resetDiscountNominal() {
    this.discount = {
      status: false,
      data: []
    };
  }
}
